import { cloneDeep } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { Group } from 'react-konva';
import Place from './components/Place/Place';
import { Props } from './interfaces';
import { recursiveTransform } from '../../core/transformCoords';
import PassWay from './components/PassWay/PassWay';
import { IPassWay } from './components/PassWay/interfaces';
import Zone from './components/Zone/Zone';
import { IZone } from './components/Zone/interfaces';
import Perimeter from './components/Perimeter/Perimeter';
import { ObjectLabel } from '../../../../../../../ObjectLabel/ObjectLabel';
import { findLabelCoords } from '../../../../../../../../../tools/findLabelCoords';
import { findPolygonCenter } from '../../../../../../../../../tools/findPolygonCenter';
import { ILayer, IPerimeter } from '../../../../../../../../../General.interfaces';
import { numberWithSpaces } from '../../../../../../../../../tools/Strings/numberWithSpaces';
import { useTranslation } from 'react-i18next';
import { allMetrics } from '../../../../../../../../../constants/metricsNames';

const getTransformedLayer = (layers: ILayer[], plan: any, layerType: string) => {
    let [actualLayer] = layers?.filter((item) => item.layer_type === layerType && item.floor === plan.floor);
    if (!actualLayer?.data) return null;
    const transLayer = cloneDeep(actualLayer);
    recursiveTransform(transLayer, plan.mainPlan.plan2geo);
    recursiveTransform(transLayer, plan.widestPlan.mainPlan.geo2plan);
    return transLayer;
};

const Geometry: React.FC<Props> = ({
    layerType = 'places_layer',
    layers = [],
    plan,
    showLabels,
    stageScale,
    widgetSettings,
    selectedGroupId,
    ...props
}) => {
    const { t } = useTranslation();

    const backPerimeter = useMemo(() => {
        const transLayer = getTransformedLayer(layers, plan, 'perimeter_layer');
        return layerType !== 'perimeter_layer'
            ? transLayer?.data.map((data: IPerimeter) => {
                  return <Perimeter object={data} heatColor={'rgba(0,0,50,0.2)'} {...props} />;
              })
            : null;
    }, [layers, plan.floor, plan.mainPlan.plan2geo, plan.widestPlan.mainPlan.geo2plan, props]);

    const transLayer = getTransformedLayer(layers, plan, layerType);
    const units = t(allMetrics?.find((m) => m.id === props.metric)?.units || '');

    const allowedMarkers: string[] = Object.keys(props.colorsByMarker?.[props.metric] || {}).map((key) => key);

    const passwayRelationGeometry = () => {
        if (!props.passwayRelationShapeMarker && props.selectedRelationId !== 'location') {
            return null;
        } else if (layerType === 'pass_ways_layer') {
            if (props.selectedRelationId === 'zone') {
                const transLayer = getTransformedLayer(layers, plan, 'zones_layer');
                let zoneObject: IZone | null = null;
                transLayer?.data.forEach((item: any) => {
                    if (item.group_marker === selectedGroupId) {
                        item.zones.forEach((object: IZone) => {
                            if (object.zone_marker === props.passwayRelationShapeMarker) {
                                zoneObject = object;
                            }
                        });
                    }
                });
                return zoneObject ? <Zone object={zoneObject} heatColor={'rgba(0,0,50,0.4)'} {...props} /> : null;
            } else if (props.selectedRelationId === 'place') {
                const transLayer = getTransformedLayer(layers, plan, 'places_layer');
                let placeObject: { [x: string]: any } | null = null;
                transLayer?.data.forEach((item: IPassWay) => {
                    if (item.marker === props.passwayRelationShapeMarker) {
                        placeObject = item;
                    }
                });

                return placeObject ? <Place object={placeObject} heatColor={'rgba(0,0,50,0.4)'} {...props} /> : null;
            } else if (props.selectedRelationId === 'location') {
                const transLayer = getTransformedLayer(layers, plan, 'perimeter_layer');
                return transLayer?.data.map((data) => {
                    return <Perimeter object={data} heatColor={'rgba(0,0,50,0.4)'} {...props} />;
                });
            } else {
                return null;
            }
        } else {
            return null;
        }
    };

    const geometry = transLayer?.data
        ?.filter((item) =>
            layerType === 'zones_layer' || layerType === 'perimeter_layer' || layerType === 'tenants_layer'
                ? true
                : allowedMarkers.includes(item.marker),
        )
        .map((data) => {
            switch (layerType) {
                case 'places_layer':
                    const heatColor =
                        props.colorsByMarker?.[props.metric]?.[data.marker]?.['heatColor'] || 'rgb(0,0,0,0.2)';
                    return <Place object={data} key={data.marker} heatColor={heatColor} {...props} />;
                case 'tenants_layer': {
                    const heatColor =
                        props.colorsByMarker?.[props.metric]?.[data.marker]?.['heatColor'] || 'rgb(0,0,0,0.2)';

                    return <Place object={data} key={data.marker} heatColor={heatColor} {...props} />;
                }
                case 'perimeter_layer': {
                    const heatColor =
                        props.colorsByMarker?.[props.metric]?.[transLayer.floor]?.['heatColor'] || 'rgb(0,0,0,0.2)';
                    return <Perimeter object={data} key={data.marker} heatColor={heatColor} {...props} />;
                }

                case 'pass_ways_layer': {
                    const heatColor =
                        props.colorsByMarker?.[props.metric]?.[data.marker]?.['heatColor'] || 'rgb(0,0,0,0.2)';
                    return (
                        <PassWay
                            object={data as IPassWay}
                            key={data.marker}
                            heatColor={heatColor}
                            planScale={plan.scale}
                            stageScale={stageScale}
                            {...props}
                        />
                    );
                }

                case 'zones_layer':
                    if (!data) {
                        return null;
                    } else {
                        if (data.group_marker === selectedGroupId) {
                            return data.zones.map((object: IZone, i: number) => {
                                const heatColor =
                                    props.colorsByMarker?.[props.metric]?.[object.zone_marker]?.['heatColor'] ||
                                    'rgb(0,0,0,0.2)';
                                return (
                                    <Zone
                                        object={object}
                                        key={`${object.zone_marker}--${object.front_id}`}
                                        heatColor={heatColor}
                                        {...props}
                                    />
                                );
                            });
                        } else {
                            return null;
                        }
                    }

                default:
                    return null;
            }
        });

    const generateValue = (str: string | number | undefined) => {
        if (str === undefined) return '';
        let result = numberWithSpaces(Number(Number(str).toFixed(2)));
        if (widgetSettings.includes('comparison')) {
            result = result + '%';
        }
        return result;
    };

    const labels = transLayer?.data
        ?.filter((item) =>
            layerType === 'zones_layer' || layerType === 'perimeter_layer'
                ? true
                : allowedMarkers.includes(item.marker),
        )
        .map((data) => {
            switch (layerType) {
                case 'places_layer':
                case 'tenants_layer': {
                    const labelCoords = findPolygonCenter(data.coords.coordinates);
                    let value = generateValue(props.colorsByMarker?.[props.metric]?.[data.marker]?.['value']);
                    const name = props.colorsByMarker?.[props.metric]?.[data.marker]?.['name'];
                    const id = props.colorsByMarker?.[props.metric]?.[data.marker]?.['id'];
                    return (
                        <ObjectLabel
                            key={id}
                            stageScale={stageScale}
                            x={labelCoords?.x}
                            y={labelCoords?.y}
                            name={name}
                            value={`${value} ${units}`}
                            selected={false}
                            planScale={plan.scale}
                        />
                    );
                }
                case 'pass_ways_layer':
                    const labelCoords = findLabelCoords(data.passLine.coordinates);
                    const name = props.colorsByMarker?.[props.metric]?.[data.marker]?.['name'];
                    const value = generateValue(props.colorsByMarker?.[props.metric]?.[data.marker]?.['value']);
                    const id = props.colorsByMarker?.[props.metric]?.[data.marker]?.['id'];
                    return (
                        <ObjectLabel
                            key={id}
                            stageScale={stageScale}
                            x={labelCoords?.x}
                            y={labelCoords?.y}
                            name={name}
                            value={`${value} ${units}`}
                            selected={false}
                            planScale={plan.scale}
                        />
                    );

                case 'zones_layer':
                    if (!data) {
                        return null;
                    } else {
                        if (data.group_marker === selectedGroupId) {
                            return data.zones.map((object: IZone, i: number) => {
                                const labelCoords = findPolygonCenter(object.coords.coordinates);
                                const name = props.colorsByMarker?.[props.metric]?.[object.zone_marker]?.['name'];
                                const value = generateValue(
                                    props.colorsByMarker?.[props.metric]?.[object.zone_marker]?.['value'],
                                );
                                const id = props.colorsByMarker?.[props.metric]?.[object.zone_marker]?.['id'] + `${i}`;
                                return (
                                    <ObjectLabel
                                        key={id}
                                        stageScale={stageScale}
                                        x={labelCoords?.x}
                                        y={labelCoords?.y}
                                        name={name}
                                        value={`${value} ${units}`}
                                        selected={false}
                                        planScale={plan.scale}
                                    />
                                );
                            });
                        } else {
                            return null;
                        }
                    }

                default:
                    return null;
            }
        });

    const overLabel = transLayer?.data
        ?.filter((item) =>
            layerType === 'zones_layer' || layerType === 'perimeter_layer'
                ? true
                : allowedMarkers.includes(item.marker),
        )
        .map((data) => {
            let value = generateValue(props.colorsByMarker?.[props.metric]?.[data.marker]?.['value']);
            switch (layerType) {
                case 'places_layer':
                case 'tenants_layer': {
                    const labelCoords = findPolygonCenter(data.coords.coordinates);
                    const name = props.colorsByMarker?.[props.metric]?.[data.marker]?.['name'];
                    const id = props.colorsByMarker?.[props.metric]?.[data.marker]?.['id'];
                    return props.overMarker === data.marker ? (
                        <ObjectLabel
                            key={id}
                            stageScale={stageScale}
                            x={labelCoords?.x}
                            y={labelCoords?.y}
                            name={name}
                            value={`${value} ${units}`}
                            selected={false}
                            planScale={plan.scale}
                        />
                    ) : null;
                }
                case 'pass_ways_layer':
                    const labelCoords = findLabelCoords(data.passLine.coordinates);
                    const name = props.colorsByMarker?.[props.metric]?.[data.marker]?.['name'];
                    const id = props.colorsByMarker?.[props.metric]?.[data.marker]?.['id'];
                    return props.overMarker === data.marker ? (
                        <ObjectLabel
                            key={id}
                            stageScale={stageScale}
                            x={labelCoords?.x}
                            y={labelCoords?.y}
                            name={name}
                            value={`${value} ${units}`}
                            selected={false}
                            planScale={plan.scale}
                        />
                    ) : null;

                case 'zones_layer':
                    if (!data) {
                        return null;
                    } else {
                        if (data.group_marker === selectedGroupId) {
                            return data.zones.map((object: IZone) => {
                                const labelCoords = findPolygonCenter(object.coords.coordinates);
                                const name = props.colorsByMarker?.[props.metric]?.[object.zone_marker]?.['name'];
                                const value = generateValue(
                                    props.colorsByMarker?.[props.metric]?.[object.zone_marker]?.['value'],
                                );
                                const id = props.colorsByMarker?.[props.metric]?.[object.zone_marker]?.['id'];
                                return props.overMarker === `${object.zone_marker}+${object.front_id}` ? (
                                    <ObjectLabel
                                        key={id}
                                        stageScale={stageScale}
                                        x={labelCoords?.x}
                                        y={labelCoords?.y}
                                        name={name}
                                        value={`${value} ${units}`}
                                        selected={false}
                                        planScale={plan.scale}
                                    />
                                ) : null;
                            });
                        } else {
                            return null;
                        }
                    }

                default:
                    return null;
            }
        });

    return (
        <Group>
            {widgetSettings.includes('showPerimeters') && backPerimeter}
            {passwayRelationGeometry()}
            {geometry}
            {showLabels && labels}
            {!showLabels && overLabel}
        </Group>
    );
};

export default Geometry;
